import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import { invalidateApiPaths } from "@/src/libs/invalidateApiPaths";
import { ROUTES } from "@/src/routes/paths";

/**
 * Les deux sanctions qu'on peut prendre depuis une fiche de compte.
 *
 * ## Elles ne sont pas de même nature, et le hook ne les mélange pas
 *
 * - **désactiver** ferme le compte sans rien détruire : le serveur refuse le
 *   login, supprime les jetons émis et coupe les WebSockets ouvertes. Le geste
 *   se défait d'un second appel, et la fiche se recharge en place ;
 * - **supprimer** est irréversible et emporte les événements organisés avec
 *   leurs fils de discussion. Il n'y a plus de fiche à recharger : l'écran
 *   **navigue vers la liste**, sans quoi il resterait à afficher un compte qui
 *   n'existe plus, puis basculerait sur son propre écran « introuvable » — ce
 *   qui se lit comme une panne alors que c'est le résultat demandé.
 *
 * ## L'invalidation nomme les deux chemins
 *
 * ⚠️ La liste **et** la fiche, explicitement : une désactivation change la
 * pastille du compte dans l'une comme l'état affiché dans l'autre, et le chemin
 * d'une clé `openapi-react-query` est comparé en entier — `/…/accounts` ne
 * couvre pas `/…/accounts/{id}`. Cf. `invalidateApiPaths`.
 */
export function useAccountActions(accountId: number) {
  const $api = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const t = useTranslations("moderation");
  const { showSuccess, showApiError } = useAppToast();

  const rafraichir = () =>
    invalidateApiPaths(queryClient, [
      "/api/v1/moderation/accounts",
      "/api/v1/moderation/accounts/{id}",
    ]);

  const status = $api.useMutation(
    "patch",
    "/api/v1/moderation/accounts/{id}/status",
    {
      onSuccess: (response) => {
        void rafraichir();

        // Le nombre de sessions coupées est dit, pas tu : « désactivé » sans
        // plus laisse croire que la personne reste connectée jusqu'à
        // l'expiration de son jeton, ce qui est précisément ce que le serveur
        // évite.
        showSuccess(
          response.data?.disabledAt ? t("disabled-done") : t("enabled-done"),
          response.data?.disabledAt
            ? t("sessions-revoked", { count: response.data.revokedTokens })
            : undefined,
        );
      },
      onError: (error) => showApiError(t("disable-error"), error),
    },
  );

  const remove = $api.useMutation(
    "delete",
    "/api/v1/moderation/accounts/{id}",
    {
      onSuccess: () => {
        void rafraichir();
        showSuccess(t("account-deleted"));
        // `replace` : le bouton « précédent » ne doit pas ramener sur la fiche
        // d'un compte supprimé.
        void navigate(ROUTES.accounts, { replace: true });
      },
      onError: (error) => showApiError(t("account-delete-error"), error),
    },
  );

  return {
    setDisabled: (disabled: boolean, reason?: string) =>
      status.mutate({
        params: { path: { id: accountId } },
        // ⚠️ Le motif n'est envoyé qu'avec une désactivation, et seulement
        // s'il a été saisi : le serveur refuserait une chaîne vide (`min(1)`),
        // et il l'ignorerait de toute façon sur une réactivation.
        body: {
          disabled,
          ...(disabled && reason?.trim() ? { reason: reason.trim() } : {}),
        },
      }),
    isSettingDisabled: status.isPending,
    remove: () => remove.mutate({ params: { path: { id: accountId } } }),
    isRemoving: remove.isPending,
  };
}
